import { LitElement, html, nothing } from 'lit';
import { customElement, property, query, queryAssignedElements, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import 'Components/atoms/vm-button/vm-button';
import 'Components/atoms/vm-icon-next/vm-icon-next';
import 'Components/atoms/vm-bottom-sheet/vm-bottom-sheet';
import { DESKTOP_BREAKPOINT_QUERY } from 'Utils/viewport';
import Styles from './vm-video-related-content.scss';

const CLAMP_HEIGHT_PROPERTY = '--vm-video-related-content-clamp-height';

const SHADOW_THRESHOLD_PX = 1;

/**
 * A titled list of related-content cards with a link to the full set.
 *
 * `max-visible-items` caps how many cards are shown: on desktop the rest are reached by
 * scrolling, with the cut edge shaded; on mobile the list is clipped outright and the CTA
 * becomes "Show all", which opens the full list in a bottom sheet. Left unset, every card
 * is listed and the section is as tall as its content.
 *
 * @slot - Default. The `vm-video-card-related` cards to list.
 */
@customElement('vm-video-related-content')
export class VmVideoRelatedContent extends LitElement {
    static override styles = Styles;

    @property({ type: String, attribute: 'section-title' }) sectionTitle = '';
    @property({ type: String, attribute: 'section-link' }) sectionLink = '';

    /** Cards to show before the list clamps. `0` (the default) lists all of them. */
    @property({ type: Number, attribute: 'max-visible-items' }) maxVisibleItems = 0;

    @property({ type: String, attribute: 'sheet-cta-label' }) sheetCtaLabel = 'See all';

    @property({ type: Boolean, attribute: 'hide-title-on-desktop', reflect: true })
    hideTitleOnDesktop = false;

    @state() private _isDesktop: boolean;
    @state() private _sheetOpen = false;
    @state() private _itemCount = 0;

    /** Copy of slotted items for bottom sheet, needed to prevent repaint on close of sheet. */
    @state() private _sheetItems: Array<HTMLElement> = [];

    /** Height of the first `maxVisibleItems` items; null while the list fits or has no layout yet. */
    @state() private _clampHeight: number | null = null;

    @state() private _shadowTop = false;
    @state() private _shadowBottom = false;

    private _mediaQuery = window.matchMedia(DESKTOP_BREAKPOINT_QUERY);
    private _resizeObserver = new ResizeObserver(() => this._measure());

    @queryAssignedElements() private _listItems!: Array<HTMLElement>;

    @query('.vm-video-related-content__list') private _list?: HTMLElement;

    @query('.vm-video-related-content__list-inner') private _listInner?: HTMLElement;

    constructor() {
        super();
        this._isDesktop = this._mediaQuery.matches;
    }

    private get _isClamped(): boolean {
        return this.maxVisibleItems > 0 && this._itemCount > this.maxVisibleItems;
    }

    private get _usesSheet(): boolean {
        return !this._isDesktop && this._isClamped;
    }

    private _onSlotChange = (): void => {
        this._measure();
    };

    private _onScroll = (): void => {
        this._updateOverflowShadows();
    };

    private _onSheetClose = (): void => {
        this._sheetOpen = false;
    };

    private _onBreakpointChange = (e: MediaQueryListEvent): void => {
        this._isDesktop = e.matches;
        // close the sheet on transition to desktop
        if (this._isDesktop) {
            this._onSheetClose();
        }
    };

    override connectedCallback(): void {
        super.connectedCallback();
        this._mediaQuery.addEventListener('change', this._onBreakpointChange);

        void this.updateComplete.then(() => {
            if (this._listInner) this._resizeObserver.observe(this._listInner);
        });
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        this._mediaQuery.removeEventListener('change', this._onBreakpointChange);
        this._resizeObserver.disconnect();
    }

    override updated(): void {
        this.style.setProperty(CLAMP_HEIGHT_PROPERTY, this._clampHeight === null ? '' : `${this._clampHeight}px`);
    }

    private _openSheet = (): void => {
        // only works for items without reactive properties
        this._sheetItems = this._listItems.map((item) => item.cloneNode(true) as HTMLElement);
        this._sheetOpen = true;
    };

    private _updateOverflowShadows(): void {
        const list = this._list;
        if (!list) {
            this._shadowTop = false;
            this._shadowBottom = false;
            return;
        }

        const { scrollTop, scrollHeight, clientHeight } = list;
        this._shadowTop = scrollTop > SHADOW_THRESHOLD_PX;
        this._shadowBottom = scrollHeight - scrollTop - clientHeight > SHADOW_THRESHOLD_PX;
    }

    /**
     * Re-reads the item count and the height of the first `maxVisibleItems`. Runs on every
     * resize of the list, so cards that upgrade from light-DOM skeletons after first paint
     * are re-measured once they have real layout.
     */
    private _measure = (): void => {
        const items = this._listItems;
        this._itemCount = items.length;

        const first = items[0];
        const last = items[this.maxVisibleItems - 1];
        if (this.maxVisibleItems <= 0 || !first || !last) {
            this._clampHeight = null;
        } else {
            const height = Math.round(last.getBoundingClientRect().bottom - first.getBoundingClientRect().top);
            // guard a list with no layout yet (pre-upgrade, skeletons)
            if (height > 0) this._clampHeight = height;
        }

        this._updateOverflowShadows();
    };

    private _renderCta() {
        if (this._usesSheet) {
            return html`
                <vm-button
                    variant="outline"
                    color-scheme="neutral"
                    class="vm-video-related-content__cta"
                    @click=${this._openSheet}
                >
                    Show all
                </vm-button>
            `;
        }

        if (!this.sectionLink) return nothing;

        return html`
            <vm-button
                variant="outline"
                color-scheme="neutral"
                class="vm-video-related-content__cta"
                href=${this.sectionLink}
            >
                See all
                <vm-icon-next name="arrow-narrow-right" slot="icon-trailing"></vm-icon-next>
            </vm-button>
        `;
    }

    private _renderList() {
        const clamped = this._clampHeight !== null && this._isClamped;
        const scrollable = clamped && this._isDesktop;

        return html`<div
            class=${classMap({
                'vm-video-related-content__list': true,
                'vm-video-related-content__list--clamped': clamped,
                'vm-video-related-content__list--shadow-top': this._shadowTop,
                'vm-video-related-content__list--shadow-bottom': this._shadowBottom,
            })}
            role=${ifDefined(scrollable ? 'region' : undefined)}
            aria-label=${ifDefined(scrollable ? this.sectionTitle || undefined : undefined)}
            @scroll=${this._onScroll}
        >
            <div class="vm-video-related-content__list-inner">
                <slot @slotchange=${this._onSlotChange}></slot>
            </div>
        </div>`;
    }

    private _renderSheet() {
        if (!this._sheetOpen) return nothing;

        return html`
            <vm-bottom-sheet open @vm-close=${this._onSheetClose} disable-backdrop>
                <span slot="header">${this.sectionTitle}</span>
                <div class="vm-video-related-content__sheet-list">${this._sheetItems}</div>
                ${this.sectionLink
                    ? html`
                          <vm-button
                              variant="outline"
                              color-scheme="neutral"
                              class="vm-video-related-content__sheet-cta"
                              href=${this.sectionLink}
                          >
                              ${this.sheetCtaLabel}
                              <vm-icon-next name="arrow-narrow-right" slot="icon-trailing"></vm-icon-next>
                          </vm-button>
                      `
                    : nothing}
            </vm-bottom-sheet>
        `;
    }

    override render() {
        return html`
            <div class="vm-video-related-content">
                ${this.sectionTitle
                    ? html`<h3 class="vm-video-related-content__title">${this.sectionTitle}</h3>`
                    : nothing}
                ${this._renderList()} ${this._renderCta()}
            </div>
            ${this._renderSheet()}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-related-content': VmVideoRelatedContent;
    }
}
